import { convertUrlParamToValue as converUrlParamToAirports } from 'component/Search/Airport/__helper/convert';
import {
    convertUrlParamToValue as convertUrlParamToAllocation,
    convertValueToOverviewData,
} from 'component/Search/Occupancy/__helper/convert';

import { defaultSearch } from 'config/SearchForm/defaults';

import DateUtils from 'utility/Format/Date/DateUtils';

import { Iata } from 'types/Search/airport';
import { AirportValue } from 'types/Search/filter';
import {
    Destination,
    DurationType,
    DurationValue,
    GeoFence,
    Occupancy,
    Search,
    SearchStep,
    Sorting,
    UnknownParams,
} from 'types/Search/form';
import {
    knownParams,
    paramDurationExactValue,
    paramTruthyValue,
    SearchParam,
} from 'types/Search/params';

import { GeoCoordinate } from 'proto/MapData/GeoCoordinate';

import { castToType, castToTypeArray } from './castToType';
import { convertUrlToFilter } from './urlToFilter';

export const pathNameToSearchStepMap: Record<string, SearchStep> = {
    '/suche/region': SearchStep.Region,
    '/suche/hotel': SearchStep.Hotel,
    '/suche/angebot': SearchStep.Offer,
};

const convertToDestination = (
    urlParams: URLSearchParams,
    pathName: string,
    destinationLabel?: string
) => {
    const hotelId = urlParams.get(SearchParam.HotelId);

    const destination: Destination = {
        hotelId: hotelId ? parseInt(hotelId) : undefined,
        cityIdList: new Set(
            castToTypeArray(urlParams.get(SearchParam.CityId)?.split(',')) ?? []
        ),
        directSearch: castToType(urlParams.get(SearchParam.DirectSearch)),
        countryIdList: new Set(
            castToTypeArray(urlParams.get(SearchParam.CountryId)?.split(',')) ??
                []
        ),
        regionGroupIdList: new Set(
            castToTypeArray(
                urlParams.get(SearchParam.AreaGroupId)?.split(',')
            ) ?? []
        ),
        regionIdList: new Set(
            castToTypeArray(urlParams.get(SearchParam.AreaId)?.split(',')) ?? []
        ),
        ctsDestination: castToType(urlParams.get(SearchParam.CtsDestination)),
        flightDistance: castToType(urlParams.get(SearchParam.FlightDistance)),
        directHotelSearchId: castToType(
            urlParams.get(SearchParam.DirectHotelSearchId)
        ),
        searchStep: pathNameToSearchStepMap[pathName],
        label: destinationLabel,
        geoFence: convertToGeoFence(urlParams),
    };

    return destination;
};

const convertToGeoFence = (
    urlParams: URLSearchParams
): GeoFence | undefined => {
    const geoFenceParam = urlParams.get(SearchParam.GeoFence);

    if (!geoFenceParam) {
        return undefined;
    }

    const [northEast, southWest, zoomValue] = geoFenceParam.split('_');

    if (!northEast || !southWest || !zoomValue) {
        return undefined;
    }

    const northEastCoordinate = convertToGeoCoordinate(northEast);
    const southWestCoordinate = convertToGeoCoordinate(southWest);

    if (!northEastCoordinate || !southWestCoordinate) {
        return undefined;
    }

    const zoom = parseInt(zoomValue);

    if (isNaN(zoom)) {
        return undefined;
    }

    return {
        northEast: northEastCoordinate,
        southWest: southWestCoordinate,
        zoom,
    };
};

const convertToGeoCoordinate = (
    geoCoordinate: string
): GeoCoordinate | undefined => {
    const [lat, long] = geoCoordinate.split(',');

    if (!lat || !long) {
        return undefined;
    }

    const latitude = parseFloat(lat);
    const longitude = parseFloat(long);

    if (isNaN(latitude) || isNaN(longitude)) {
        return undefined;
    }

    return {
        latitude,
        longitude,
    };
};

const convertToDepartureAirportList = (
    urlParams: URLSearchParams
): AirportValue<Iata> => {
    const airportParam = urlParams.get(SearchParam.Airport);
    if (airportParam) {
        return converUrlParamToAirports(airportParam);
    }

    return new Set();
};

const convertToOccupancy = (urlParams: URLSearchParams): Occupancy => {
    const roomAllocationParam = urlParams.get(SearchParam.RoomAllocation);
    if (!roomAllocationParam) {
        return defaultSearch.occupancy;
    }
    const roomAllocation = convertUrlParamToAllocation(roomAllocationParam);
    const overview = convertValueToOverviewData(roomAllocation);
    return {
        adultCount: overview.adultCount,
        childCount: overview.childAgeList.length,
        allocation: roomAllocation,
    };
};

const parseDurationValue = (duration: string | null): DurationValue => {
    if (!duration || duration === '-' || duration.includes('NaN')) {
        return {
            type: DurationType.Empty,
        };
    }
    if (duration === paramDurationExactValue) {
        return {
            type: DurationType.Exact,
        };
    }
    if (duration.endsWith('w')) {
        return {
            type: DurationType.Week,
            amount: parseInt(duration.replace('w', '')),
        };
    }
    if (duration.includes('-')) {
        const [min, max] = duration.split('-');
        return {
            type: DurationType.Range,
            min: parseInt(min),
            max: parseInt(max),
        };
    }
    return {
        type: DurationType.Day,
        amount: parseInt(duration),
    };
};

const convertToTravelPeriod = (urlParams: URLSearchParams) => {
    const departureDateParam = urlParams.get(SearchParam.DepartureDate) ?? '';
    const departureDate = new Date(departureDateParam);
    const returnDateParam = urlParams.get(SearchParam.ReturnDate) ?? '';
    const returnDate = new Date(returnDateParam);
    const daysParam = urlParams.get(SearchParam.Days);

    return {
        departureDate: DateUtils.isValid(departureDate)
            ? departureDate
            : undefined,
        returnDate: DateUtils.isValid(returnDate) ? returnDate : undefined,
        duration: parseDurationValue(daysParam),
    };
};

const convertSortingToUrlParams = (urlParams: URLSearchParams): Sorting => {
    const regionParam = urlParams.get(SearchParam.SortRegion);
    const hotelParam = urlParams.get(SearchParam.SortHotel);
    const offerParam = urlParams.get(SearchParam.SortOffer);

    return {
        region: castToType(regionParam),
        hotel: castToType(hotelParam),
        offer: castToType(offerParam),
    };
};

const buildUnknownParams = (urlParams: URLSearchParams): UnknownParams => {
    const unknownParams: UnknownParams = {};
    urlParams.forEach((value, param) => {
        if (!knownParams.has(param)) {
            unknownParams[param] = value;
        }
    });

    return unknownParams;
};

interface ConvertUrlToSearchProps {
    searchUrl: string;
    destinationLabel?: string;
    pageCount?: number;
}

export const convertUrlToSearch = ({
    searchUrl,
    destinationLabel,
    pageCount = 1,
}: ConvertUrlToSearchProps): Search => {
    const url = new URL(searchUrl);
    const searchUrlParams = url.searchParams;
    const pathName = url.pathname;

    return {
        destination: convertToDestination(
            searchUrlParams,
            pathName,
            destinationLabel
        ),
        departureAirportList: convertToDepartureAirportList(searchUrlParams),
        occupancy: convertToOccupancy(searchUrlParams),
        travelPeriod: convertToTravelPeriod(searchUrlParams),
        pagination: {
            currentPage: parseInt(
                searchUrlParams.get(SearchParam.Page) ?? paramTruthyValue
            ),
            pageCount,
        },
        sorting: convertSortingToUrlParams(searchUrlParams),
        filter: convertUrlToFilter(searchUrl),
        unknownParams: buildUnknownParams(searchUrlParams),
    };
};
